import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { collection, query, orderBy, getDocs, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import useSelectedDevice from '../hooks/useSelectedDevice';
import useStallRecovery from '../hooks/useStallRecovery';
import { APP_RESUME_EVENT } from '../connection';
import usePullToRefresh from '../hooks/usePullToRefresh';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import { maToFeet } from '../utils/cal';
import { withRetry, cachedDocs, cachedDoc } from '../utils/firestoreRetry';
import { useLeakRegions } from '../hooks/useLeakData';
import PlusPromptModal from '../components/PlusPromptModal';
import SharingPaused from '../components/SharingPaused';
import { tierOf, plusIsOn } from '../utils/plusStatus';
import useUserPrefs from '../hooks/useUserPrefs';

// Ranges a Free monitor can show (the rest are Plus).
const FREE_RANGES = ['48h', '1w'];

const RANGES = [
  { label: '12h',  value: '12h',  hours: 12,   daily: false, source: 'fivemin' },
  { label: '24h',  value: '24h',  hours: 24,   daily: false, source: 'fivemin' },
  { label: '48h',  value: '48h',  hours: 48,   daily: false, source: 'hourly'  },
  { label: '1 wk', value: '1w',   hours: 168,  daily: false, source: 'hourly'  },
  { label: '1 mo', value: '1m',   hours: 720,  daily: true,  source: 'daily'   },
  { label: '6 mo', value: '6m',   hours: 4320, daily: true,  source: 'daily'   },
];

// How much of the recent edge the 12h/24h views pull from raw readings and
// stitch onto the fivemin_2d aggregate body. The aggregate only refreshes on
// the hourly server rollup, so it can trail "now" by up to ~1h; this live
// tail keeps the right edge current. 90 min covers the worst-case rollup lag
// with margin. Raw docs in the tail: ~180 at 30s cadence, ~18 at 5-min.
const TAIL_MS = 90 * 60 * 1000;

// Event type → display config.
//   0 = calibration (confirmed)
//   1 = unknown / reserved
//   2 = unknown — frequently logged, may be reconnect/heartbeat
//   TODO Session 2: confirm event type meanings from firmware source.
const EVENT_TYPES = {
  0: { label: 'Calibration', color: 'driftwood', dashed: true,  show: true  },
  1: { label: 'Event',       color: 'driftwood', dashed: true,  show: false },
  2: { label: 'Event',       color: 'driftwood', dashed: true,  show: false },
};

const METRICS = [
  { label: 'Depth',   value: 'depth'   },
  { label: 'Percent', value: 'percent' },
  { label: 'Gallons', value: 'gallons' },
];
const METRIC_UNIT  = { depth: 'ft', percent: '%',          gallons: 'gal'    };
const METRIC_LABEL = { depth: 'Depth', percent: 'Tank level', gallons: 'Volume' };

function fmtAxis(v, unit) {
  if (unit === '%')   return `${Math.round(v)}%`;
  if (unit === 'ft')  return Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1);
  if (unit === 'gal') return Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${Math.round(v)}`;
  return `${Math.round(v)}`;
}

// "Nice" axis step (1/2/5 × 10^n) closest to the ideal for `target` ticks, so
// Y labels land on round numbers (even feet, 0/25/50…, 0/500/1000…) instead of
// awkward fractions from dividing the range into equal parts.
function niceStep(range, target) {
  if (range <= 0) return 1;
  const rough = range / target;
  const pow   = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm  = rough / pow;
  const mult  = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
  return mult * pow;
}

// Round tick values spanning [min, max] at a nice step.
function niceTicks(min, max, target) {
  const step  = niceStep(max - min, target);
  const start = Math.ceil(min / step - 1e-9) * step;
  const ticks = [];
  for (let v = start; v <= max + 1e-9; v += step) {
    // Kill floating-point dust so labels read "2" not "1.9999999".
    ticks.push(Math.abs(v) < step / 1e6 ? 0 : v);
  }
  return { ticks, step };
}

// Absolute time for the hover card. Clearer than "1d 19h ago": shows the date,
// plus the time for sub-daily points (daily-average points show date only).
function formatAbsolute(ts, range) {
  const d    = new Date(ts);
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  if (range && range.daily) return date;
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date}, ${time}`;
}

// X-axis tick label as an array of lines. Week/month/6-month ranges → a numeric
// date "8/27". Hour ranges → a clock time "4 PM"; the 48h view adds the date on
// a line above it, since a bare "4 PM" repeats across the two days and is
// ambiguous.
function axisLabelLines(ts, range) {
  const d = new Date(ts);
  const date = d.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
  if (range && (range.daily || range.value === '1w')) return [date];
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric' });
  if (range && range.value === '48h') return [date, time];
  return [time];
}

// Depth formatter — mirror of Dashboard's formatDepth. Honors the user's
// depthFormat pref and rounds inches to the nearest 0.25".
function formatDepth(depthFt, format) {
  const sign = depthFt < 0 ? '\u2212' : '';
  const abs  = Math.abs(depthFt);
  if (format === 'ft-decimal') return `${sign}${abs.toFixed(1)} ft`;
  if (format === 'inches') {
    const inches = Math.round(abs * 12 * 4) / 4;
    return `${sign}${inches} in`;
  }
  const totalInches = abs * 12;
  const ft = Math.floor(totalInches / 12);
  const inches = Math.round((totalInches - ft * 12) * 4) / 4;
  if (inches >= 12) return `${sign}${ft + 1} ft 0 in`;
  if (ft === 0)     return `${sign}${inches} in`;
  return `${sign}${ft} ft ${inches} in`;
}

function fmtValue(v, metric, depthFormat) {
  if (metric === 'percent') return `${Math.round(v)}%`;
  if (metric === 'gallons') return `${Math.round(v).toLocaleString()} gal`;
  return formatDepth(v, depthFormat);
}

// Which metrics a tank can display, mirroring the dashboard's state machine.
function tankMetricAvail(tank) {
  const hasCal = Array.isArray(tank?.calPoints) && tank.calPoints.length >= 2;
  const hasMa  = typeof tank?.emptyMa === 'number' && typeof tank?.fullMa === 'number';
  const cap    = tank?.capacity ?? 0;
  return { depth: hasCal, percent: hasMa, gallons: hasMa && cap > 0 };
}

// Convert one stored point to the selected metric's units for tank idx (1|2).
function seriesValue(pt, idx, metric, dev) {
  const pct  = idx === 1 ? pt.v1 : pt.v2;
  const mA   = idx === 1 ? pt.m1 : pt.m2;
  const tank = idx === 1 ? dev?.tank1 : dev?.tank2;
  const cap  = tank?.capacity ?? 0;
  if (metric === 'percent') return pct ?? null;
  if (metric === 'gallons') {
    if (cap <= 0) return null;
    // Derive gallons straight from the sensor mA — the same source the depth
    // line uses — so a 0.25 in depth change and the gallons change stay locked
    // together. The stored on-device percent is rounded separately, which made
    // gallons step down in bigger jumps than depth.
    //   usableFt = maToFeet(mA) - emptyFt   (clamped >= 0)
    //   gallons  = usableFt / fullFt * capacity
    if (mA != null && Array.isArray(tank?.calPoints)
        && typeof tank?.emptyFt === 'number'
        && typeof tank?.fullFt === 'number' && tank.fullFt > 0) {
      const absFt = maToFeet(mA, tank.calPoints);
      if (absFt != null) return Math.max(0, absFt - tank.emptyFt) / tank.fullFt * cap;
    }
    // Fallback: daily aggregates (percent only, no mA) and uncalibrated tanks.
    if (pct == null) return null;
    const uf = (typeof tank?.fullFt === 'number' && tank.fullFt > 0 && typeof tank?.emptyFt === 'number')
      ? (tank.fullFt - tank.emptyFt) / tank.fullFt : 1;
    return Math.max(0, pct) / 100 * cap * uf;
  }
  if (mA != null) {
    const absFt = maToFeet(mA, tank?.calPoints);
    if (absFt == null) return null;
    return typeof tank?.emptyFt === 'number' ? absFt - tank.emptyFt : Math.max(0, absFt);
  }
  if (pct != null && typeof tank?.emptyFt === 'number' && typeof tank?.fullFt === 'number') {
    return (tank.fullFt - tank.emptyFt) * pct / 100;
  }
  return null;
}

function cssVar(name, fallback = '#000') {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function hexToRgbTriple(hex) {
  const m = hex.replace('#', '').match(/.{2}/g);
  if (!m) return '0,0,0';
  return m.map(h => parseInt(h, 16)).join(', ');
}

// Given a cursor X (canvas coords) and the data array, find the nearest
// data point by timestamp. Returns the matching index, or -1.
function findNearestIndex(data, cursorTs) {
  if (!data || data.length === 0) return -1;
  let lo = 0, hi = data.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (data[mid].ts < cursorTs) lo = mid + 1;
    else hi = mid;
  }
  // lo is the smallest index with ts >= cursorTs.
  // Compare to lo-1 to find which is closer.
  if (lo === 0) return 0;
  const before = data[lo - 1];
  const after  = data[lo];
  return Math.abs(before.ts - cursorTs) < Math.abs(after.ts - cursorTs)
    ? lo - 1
    : lo;
}

// Generate labeled ("major") and unlabeled ("minor") time-axis ticks, anchored
// to clean wall-clock boundaries so labels read "4 PM" / "8/27" (never "3:47"
// or "6h ago"). Hour ranges step in whole hours with minor marks between the
// labels for denser reference; the 1-week view labels all 7 days; month/6-month
// step in even day increments. Returns { majors, minors } within [tsOldest,
// tsNewest]; the caller labels only majors.
function computeTimeTicks(range, tsOldest, tsNewest, cw) {
  const HOUR = 3600000, DAY = 86400000;
  const useDays = range.daily || range.value === '1w';
  const unitMs  = useDays ? DAY : HOUR;

  let step; // in units (hours or days)
  if (range.value === '1w') {
    step = 1;                                    // every day — all 7 labeled
  } else if (useDays) {
    const target  = Math.max(4, Math.min(8, Math.floor(cw / 50)));
    const allowed = [1, 2, 3, 7, 14, 30, 60];
    const raw = ((tsNewest - tsOldest) / unitMs) / target;
    step = allowed.find(s => s >= raw) || 60;
  } else {
    const target  = Math.max(5, Math.min(10, Math.floor(cw / 50)));  // denser hours
    const allowed = [1, 2, 3, 4, 6, 8, 12];
    const raw = ((tsNewest - tsOldest) / unitMs) / target;
    step = allowed.find(s => s >= raw) || 12;
  }
  const stepMs = step * unitMs;

  // Anchor the rightmost tick to a clean boundary at/just before the newest
  // point. Hours snap to an even step-hour of the local day; days to midnight.
  const anchor = new Date(tsNewest);
  if (useDays) {
    anchor.setHours(0, 0, 0, 0);
  } else {
    anchor.setMinutes(0, 0, 0);
    anchor.setHours(Math.floor(anchor.getHours() / step) * step);
  }
  const start = anchor.getTime();

  const collect = (spanMs, cap) => {
    const out = [];
    for (let t = start; t >= tsOldest && out.length < cap; t -= spanMs) {
      if (t <= tsNewest) out.push(t);
    }
    return out.reverse();
  };

  const majors = collect(stepMs, 40);
  // Minor marks only on the hour ranges (half-step); date ranges stay clean.
  const minors = useDays ? [] : collect(stepMs / 2, 80);
  return { majors, minors };
}

// ── Canvas chart renderer ──────────────────────────────────────────────────

function drawChart(canvas, data, showT1, showT2, events, range, cursorIdx, yUnit, yFullScale, regions) {
  if (!canvas || !data || data.length === 0) return;
  const ctx = canvas.getContext('2d');
  // Render at device pixel ratio so text/lines are crisp on hi-DPI screens
  // (phones, retina) instead of being drawn at 1x and scaled up (blurry). We
  // draw in CSS pixels; the transform maps them to the device-res backing store.
  const dpr = window.devicePixelRatio || 1;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const W   = canvas.width  / dpr;
  const H   = canvas.height / dpr;
  const pad = { t: 20, r: 12, b: 28, l: 38 };
  const cw  = W - pad.l - pad.r;
  const ch  = H - pad.t - pad.b;
  const n   = data.length;

  const ink       = cssVar('--color-ink',       '#0B3A4A');
  const reef      = cssVar('--color-reef',      '#2E7A7F');
  const moss      = cssVar('--color-moss',      '#556B4F');
  const inkRgb    = hexToRgbTriple(ink);
  const reefRgb   = hexToRgbTriple(reef);
  const mossRgb   = hexToRgbTriple(moss);

  ctx.clearRect(0, 0, W, H);

  const visibleVals = [];
  data.forEach(pt => {
    if (showT1 && pt.v1 != null) visibleVals.push(pt.v1);
    if (showT2 && pt.v2 != null) visibleVals.push(pt.v2);
  });
  let yMin, yMax;
  if (yFullScale != null && yFullScale > 0) {
    // Always frame against the tank's full scale (100%, full depth, or
    // capacity) so a stable half-full tank reads as half full — not "full"
    // from auto-zooming, which was confusing people. Expand only if real data
    // runs past the nominal bounds (overfill above full, or below empty) so
    // nothing gets clipped.
    const dataMin = visibleVals.length ? Math.min(...visibleVals) : 0;
    const dataMax = visibleVals.length ? Math.max(...visibleVals) : yFullScale;
    yMin = Math.min(0, dataMin);
    yMax = Math.max(yFullScale, dataMax);
  } else if (visibleVals.length > 0) {
    // Full scale unknown (tank not fully set up): auto-range around the data.
    const dataMin = Math.min(...visibleVals);
    const dataMax = Math.max(...visibleVals);
    const padV    = Math.max((dataMax - dataMin) * 0.12, yUnit === 'ft' ? 0.25 : (yUnit === '%' ? 5 : 1));
    yMin = yUnit === 'gal' ? 0 : Math.min(0, dataMin - padV);
    yMax = dataMax + padV;
    if (yMax - yMin < 1e-6) yMax = yMin + 1;
  } else {
    yMin = 0; yMax = 1;
  }
  const yRange = yMax - yMin;

  // Y grid + labels at "nice" round values (even feet, 0/25/50…, 0/500/1000…)
  // so the axis reads cleanly. Minor gridlines at half-step add extra
  // horizontal reference lines without labels.
  const yPosLin = v => pad.t + ch - ((v - yMin) / yRange) * ch;
  const { ticks: yTicks, step: yStep } = niceTicks(yMin, yMax, 4);

  // Minor gridlines (half-step) — lighter, unlabeled.
  ctx.strokeStyle = `rgba(${inkRgb}, 0.06)`;
  ctx.lineWidth   = 1;
  const yMinorStep = yStep / 2;
  for (let v = Math.ceil(yMin / yMinorStep - 1e-9) * yMinorStep; v <= yMax + 1e-9; v += yMinorStep) {
    // Skip minor lines that land on a major tick.
    if (Math.abs(v / yStep - Math.round(v / yStep)) < 1e-6) continue;
    const y = yPosLin(v);
    ctx.beginPath();
    ctx.moveTo(pad.l, y);
    ctx.lineTo(pad.l + cw, y);
    ctx.stroke();
  }

  // Major gridlines + labels.
  ctx.font      = '600 11px Inter, sans-serif';
  ctx.textAlign = 'right';
  yTicks.forEach(v => {
    const y = yPosLin(v);
    ctx.strokeStyle = `rgba(${inkRgb}, 0.15)`;
    ctx.lineWidth   = 1;
    ctx.beginPath();
    ctx.moveTo(pad.l, y);
    ctx.lineTo(pad.l + cw, y);
    ctx.stroke();
    ctx.fillStyle = ink;
    ctx.fillText(fmtAxis(v, yUnit), pad.l - 4, y + 3);
  });

  const tsOldest = data[0].ts;
  const tsNewest = data[n - 1].ts;
  const tsSpan   = Math.max(tsNewest - tsOldest, 1);

  const xPosTs = ts => pad.l + ((ts - tsOldest) / tsSpan) * cw;
  const yPos   = v  => {
    const clamped = Math.min(Math.max(v, yMin), yMax);
    return pad.t + ch - ((clamped - yMin) / yRange) * ch;
  };

  // X grid ticks. Minor marks (hour ranges only) are lighter and unlabeled;
  // major marks are labeled below. Vertical gridlines sit behind the data.
  const { majors: timeTicks, minors: timeMinors } = computeTimeTicks(range, tsOldest, tsNewest, cw);
  ctx.lineWidth = 1;
  timeMinors.forEach(ts => {
    const x = xPosTs(ts);
    ctx.strokeStyle = `rgba(${inkRgb}, 0.06)`;
    ctx.beginPath();
    ctx.moveTo(x, pad.t);
    ctx.lineTo(x, pad.t + ch);
    ctx.stroke();
  });
  timeTicks.forEach(ts => {
    const x = xPosTs(ts);
    ctx.strokeStyle = `rgba(${inkRgb}, 0.13)`;
    ctx.beginPath();
    ctx.moveTo(x, pad.t);
    ctx.lineTo(x, pad.t + ch);
    ctx.stroke();
  });

  // Axis baselines (left + bottom) — darker than the gridlines so the plot
  // frame reads clearly.
  ctx.strokeStyle = `rgba(${inkRgb}, 0.4)`;
  ctx.lineWidth   = 1.5;
  ctx.beginPath();
  ctx.moveTo(pad.l, pad.t);
  ctx.lineTo(pad.l, pad.t + ch);
  ctx.lineTo(pad.l + cw, pad.t + ch);
  ctx.stroke();

  // Y-axis unit caption (ft / gal) above the number column. Percent needs none
  // (the % is on each tick). Depth is the intuitive one; gallons gets a hint.
  if (yUnit === 'ft' || yUnit === 'gal') {
    ctx.fillStyle = ink;
    ctx.font      = '600 10px Inter, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(yUnit, pad.l - 4, 10);
  }

  function drawLine(key, lineColor, fillRgb) {
    const grad = ctx.createLinearGradient(0, pad.t, 0, pad.t + ch);
    grad.addColorStop(0, `rgba(${fillRgb}, 0.22)`);
    grad.addColorStop(1, `rgba(${fillRgb}, 0)`);

    const segments = [];
    let current = [];
    data.forEach(pt => {
      if (pt[key] == null) {
        if (current.length > 0) { segments.push(current); current = []; }
      } else {
        current.push(pt);
      }
    });
    if (current.length > 0) segments.push(current);

    segments.forEach(seg => {
      if (seg.length === 0) return;

      ctx.beginPath();
      ctx.moveTo(xPosTs(seg[0].ts), pad.t + ch);
      seg.forEach(pt => ctx.lineTo(xPosTs(pt.ts), yPos(pt[key])));
      ctx.lineTo(xPosTs(seg[seg.length - 1].ts), pad.t + ch);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();

      ctx.beginPath();
      seg.forEach((pt, i) => {
        const x = xPosTs(pt.ts);
        const y = yPos(pt[key]);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.strokeStyle = lineColor;
      ctx.lineWidth   = 2;
      ctx.lineJoin    = 'round';
      ctx.stroke();
    });
  }

  // Leak regions — shaded band BEHIND the data (endpoint verticals are drawn
  // after the lines so they stay crisp). Lighter orange = slow leak, darker =
  // rapid / heavy use. endTs null = ongoing → band runs to the right edge.
  const leakRgb = {
    slow:  hexToRgbTriple(cssVar('--color-copper-soft', '#C97149')),
    away:  hexToRgbTriple(cssVar('--color-copper-soft', '#C97149')), // same as slow
    rapid: hexToRgbTriple(cssVar('--color-copper',      '#B8552A')),
  };
  (regions || []).forEach(rg => {
    const s = Math.max(rg.startTs, tsOldest);
    const e = Math.min(rg.endTs == null ? tsNewest : rg.endTs, tsNewest);
    if (e <= tsOldest || s >= tsNewest || e <= s) return;
    const x0 = xPosTs(s);
    const x1 = xPosTs(e);
    ctx.fillStyle = `rgba(${leakRgb[rg.kind] || leakRgb.slow}, ${rg.kind === 'rapid' ? 0.30 : 0.10})`;
    ctx.fillRect(x0, pad.t, Math.max(1.5, x1 - x0), ch);
    // Rapid regions: label the band with the total gallons lost (if it fits).
    if (rg.kind === 'rapid' && rg.observedGal > 0 && (x1 - x0) >= 34) {
      ctx.save();
      ctx.fillStyle = `rgba(${leakRgb.rapid}, 0.95)`;
      ctx.font = '600 10px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(`−${Math.round(rg.observedGal)} gal`, (x0 + x1) / 2, pad.t + 3);
      ctx.restore();
    }
  });

  if (showT1) drawLine('v1', reef, reefRgb);
  if (showT2) drawLine('v2', moss, mossRgb);

  // Leak region endpoint verticals (dashed). Both ends for a closed region;
  // start only for an ongoing one (its band already runs to the right edge).
  (regions || []).forEach(rg => {
    const rgb = leakRgb[rg.kind] || leakRgb.slow;
    const drawEdge = ts => {
      if (ts < tsOldest || ts > tsNewest) return;
      const x = xPosTs(ts);
      ctx.save();
      ctx.strokeStyle = `rgba(${rgb}, 0.95)`;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 3]);
      ctx.beginPath();
      ctx.moveTo(x, pad.t);
      ctx.lineTo(x, pad.t + ch);
      ctx.stroke();
      ctx.restore();
    };
    drawEdge(rg.startTs);
    if (rg.endTs != null) drawEdge(rg.endTs);
  });

  // An event in progress that has gone quiet: mark its last drop faintly, so
  // the stop shows as it happens. It isn't drawn as an end until it settles.
  (regions || []).forEach(rg => {
    if (rg.endTs != null || rg.provisionalEndTs == null) return;
    const ts = rg.provisionalEndTs;
    if (ts < tsOldest || ts > tsNewest) return;
    const x = xPosTs(ts);
    ctx.save();
    ctx.strokeStyle = `rgba(${leakRgb[rg.kind] || leakRgb.slow}, 0.45)`;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([2, 4]);
    ctx.beginPath();
    ctx.moveTo(x, pad.t);
    ctx.lineTo(x, pad.t + ch);
    ctx.stroke();
    ctx.restore();
  });

  // Event markers
  events.forEach(ev => {
    if (ev.ts < tsOldest || ev.ts > tsNewest) return;
    const cfg = EVENT_TYPES[ev.type];
    if (!cfg || !cfg.show) return;
    const x = xPosTs(ev.ts);
    ctx.save();
    const colorVal = cssVar(`--color-${cfg.color}`, '#5F5E5A');
    ctx.strokeStyle = `rgba(${hexToRgbTriple(colorVal)}, 0.6)`;
    ctx.lineWidth   = 1;
    if (cfg.dashed) ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(x, pad.t);
    ctx.lineTo(x, pad.t + ch);
    ctx.stroke();
    ctx.restore();
  });

  // Cursor indicator (vertical line + dots on intersected series)
  if (cursorIdx != null && cursorIdx >= 0 && cursorIdx < n) {
    const pt = data[cursorIdx];
    const x = xPosTs(pt.ts);
    ctx.save();
    ctx.strokeStyle = ink;
    ctx.lineWidth   = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(x, pad.t);
    ctx.lineTo(x, pad.t + ch);
    ctx.stroke();
    ctx.restore();

    // Dots on the line(s) at this point
    if (showT1 && pt.v1 != null) {
      ctx.beginPath();
      ctx.arc(x, yPos(pt.v1), 4, 0, Math.PI * 2);
      ctx.fillStyle = reef;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    if (showT2 && pt.v2 != null) {
      ctx.beginPath();
      ctx.arc(x, yPos(pt.v2), 4, 0, Math.PI * 2);
      ctx.fillStyle = moss;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }

  // Time axis labels on the major ticks. Interior ticks center under their
  // gridline; edge ticks align inward so they don't clip. Hour ranges show a
  // right-edge "Now" (and drop a label that would collide with it); date ranges
  // label every date, no "Now".
  const useDaysAxis = range.daily || range.value === '1w';
  // Show a right-edge "Now" on the hour ranges and the 1-week view (not on the
  // month/6-month views, where the trailing date reads fine on its own).
  const showNow = !useDaysAxis || range.value === '1w';
  ctx.fillStyle = ink;
  ctx.font      = '600 10px Inter, sans-serif';
  timeTicks.forEach(ts => {
    const x = xPosTs(ts);
    if (showNow && x > pad.l + cw - 26) return;
    const lines = axisLabelLines(ts, range);
    // Edge ticks align inward so they don't clip; interior ticks center.
    const tx = x < pad.l + 14 ? pad.l : (x > pad.l + cw - 14 ? pad.l + cw : x);
    ctx.textAlign = x < pad.l + 14 ? 'left' : (x > pad.l + cw - 14 ? 'right' : 'center');
    // Stack multi-line labels (48h: date over time); single line sits at H-5.
    if (lines.length === 2) {
      ctx.fillText(lines[0], tx, H - 16);
      ctx.fillText(lines[1], tx, H - 5);
    } else {
      ctx.fillText(lines[0], tx, H - 5);
    }
  });
  if (showNow) {
    ctx.textAlign = 'right';
    ctx.fillText('Now', pad.l + cw, H - 5);
  }
}

// Translate a cursor index into the chart's pixel X position. Mirrors
// the math inside drawChart so the tooltip can be positioned correctly.
function cursorPixelX(canvas, data, cursorIdx) {
  if (!canvas || cursorIdx == null || cursorIdx < 0 || cursorIdx >= data.length) return null;
  const pad = { t: 20, r: 12, b: 28, l: 38 };
  // CSS-pixel width (canvas.width is the device-scaled backing store).
  const cw = canvas.offsetWidth - pad.l - pad.r;
  const tsOldest = data[0].ts;
  const tsNewest = data[data.length - 1].ts;
  const tsSpan   = Math.max(tsNewest - tsOldest, 1);
  return pad.l + ((data[cursorIdx].ts - tsOldest) / tsSpan) * cw;
}

// ── Main Trends page ───────────────────────────────────────────────────────

export default function Trends({ user }) {
  const { prefs } = useUserPrefs(user);
  const canvasRef               = useRef(null);
  const wrapRef                 = useRef(null);
  const [devices, setDevices]   = useState([]);
  const [selected, setSelected] = useSelectedDevice(user, devices);
  const [range, setRange]       = useState(
    () => RANGES.find(r => r.value === localStorage.getItem('trends.range')) || RANGES[2]
  );
  const [showT1, setShowT1]     = useState(true);
  const [showT2, setShowT2]     = useState(true);
  const [rawData, setData]    = useState([]);
  const [events, setEvents]     = useState([]);
  // Start true so the chart shows a spinner until the first fetch completes,
  // instead of flashing "No data yet" before the device list / selection has
  // even resolved. Cleared below if there are no devices to fetch.
  const [loading, setLoading]   = useState(true);
  // Only treat it as a "stuck load" (spinner + auto-reload) when there's nothing
  // on screen. During a refetch/resume we keep the previous chart, so a slow
  // background refresh never blanks the page or triggers a reload.
  const slowLoad = useStallRecovery(loading && rawData.length === 0);
  const [hasT2, setHasT2]       = useState(false);

  // Cursor state. cursorIdx = current data index under cursor (live hover or pinned).
  // pinned = true means user has clicked/tapped to lock it; clicking again clears.
  const [cursorIdx, setCursorIdx] = useState(null);
  const [pinned, setPinned]       = useState(false);
  const [metric, setMetric]       = useState(
    () => localStorage.getItem('trends.metric') || 'percent'
  );

  // Persist pill selections so they survive navigating away and back.
  useEffect(() => { localStorage.setItem('trends.range', range.value); }, [range]);
  useEffect(() => { localStorage.setItem('trends.metric', metric); }, [metric]);

  // Load user devices
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const claimsCol = collection(db, 'users', user.uid, 'devices');
    // v8 schema: claims live at users/{uid}/devices/. For each claim, fetch
    // the matching device doc for name + tank info needed by the charts.
    const buildList = (claimsSnap, useCache) => Promise.all(claimsSnap.docs.map(async claimDoc => {
      const claim = { id: claimDoc.id, ...claimDoc.data() };
      const ref = doc(db, 'devices', claim.deviceId);
      const devSnap = useCache ? await cachedDoc(ref) : await withRetry(() => getDoc(ref)).catch(() => null);
      const dev = (devSnap && devSnap.exists()) ? devSnap.data() : {};
      return {
        ...claim,
        name:          dev.name           || claim.deviceId,
        tank1Name:     dev.tank1?.name    || 'Tank 1',
        tank2Name:     dev.tank2?.name    || 'Tank 2',
        tank1Capacity: dev.tank1?.capacity ?? 0,
        tank2Capacity: dev.tank2?.capacity ?? 0,
        tank1:         dev.tank1 || null,
        tank2:         dev.tank2 || null,
        leakDetection: dev.leakDetection === true,
        // Free tier: what the tier depends on, and the 06:00 snapshot the
        // chart stops at.
        plus:          dev.plus || null,
        claimed:       dev.claimed === true,
        claimedAt:     dev.claimedAt || null,
        dailyReading:  dev.dailyReading || null,
        ownerEmail:    dev.ownerEmail || null,
        // Live rapid-leak state from the same read (no extra cost): the event
        // in progress per tank and the last one that settled.
        usageEpisode:     dev.usageEpisode || null,
        usageLastSettled: dev.usageLastSettled || null,
      };
    }));
    (async () => {
      // Cache-first paint: show the picker instantly if IndexedDB has it.
      const cached = await cachedDocs(claimsCol);
      if (cached && !cancelled) {
        const cachedList = await buildList(cached, true);
        if (!cancelled) {
          setDevices(cachedList);
          if (cachedList.length === 0) setLoading(false);
        }
      }
      // Authoritative server read.
      try {
        const claimsSnap = await withRetry(() => getDocs(claimsCol));
        const list = await buildList(claimsSnap, false);
        if (!cancelled) {
          setDevices(list);
          // No devices → nothing to fetch, so stop the initial spinner
          // (fetchHistory bails when selected is null). When there ARE
          // devices, fetchHistory manages loading once selected resolves.
          if (list.length === 0) setLoading(false);
        }
      } catch (e) {
        console.error('Trends: load devices', e);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const fetchHistory = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const sinceMs = Date.now() - range.hours * 3600 * 1000;

      // Build the chart from a `reader` that returns a snapshot or null. Run
      // twice: first against the LOCAL cache (instant, never hangs — so a
      // returning session paints immediately even on a cold post-sleep
      // connection), then against the SERVER to revalidate. Same transforms
      // both ways. The aggregate strategy (one read vs. thousands of raw
      // readings) is unchanged:
      //   1mo / 6mo → daily_180d, 48h / 1wk → hourly_7d, 12h / 24h → fivemin+raw.
      const loadChart = async (reader) => {
        let chartData = [];
        let t2Present = false;

        if (range.source === 'daily' || range.source === 'hourly') {
          const aggId    = range.source === 'daily' ? 'daily_180d' : 'hourly_7d';
          const centerMs = range.source === 'daily' ? 43200000 : 1800000; // midday vs mid-hour
          const aggSnap  = await reader.getDoc(doc(db, 'devices', selected, 'aggregates', aggId));
          if (aggSnap && aggSnap.exists()) {
            const buckets = aggSnap.data().buckets || [];
            chartData = buckets
              .filter(b => b.ts >= sinceMs)
              .map(b => ({
                ts: b.ts + centerMs,
                v1: b.v1 && b.v1.n > 0 ? b.v1.avg : null,
                v2: b.v2 && b.v2.n > 0 ? b.v2.avg : null,
                // Average mA (added to buckets by the rollup) → depth/gallons come
                // from the sensor, not percent×full-height, so a full-height edit
                // doesn't rescale depth history. Null on older buckets → pct fallback.
                m1: (b.v1 && typeof b.v1.ma === 'number') ? b.v1.ma : null,
                m2: (b.v2 && typeof b.v2.ma === 'number') ? b.v2.ma : null,
                s2: !!(b.v2 && b.v2.n > 0),
              }))
              .filter(b => b.v1 != null || b.v2 != null);
            t2Present = chartData.some(b => b.s2);
          }
        } else if (range.source === 'fivemin') {
          const centerMs  = 150000;                 // 2.5 min — center in 5-min bucket
          const tailStart = Date.now() - TAIL_MS;
          const aggSnap   = await reader.getDoc(doc(db, 'devices', selected, 'aggregates', 'fivemin_2d'));
          const aggExists = !!(aggSnap && aggSnap.exists());
          let body = [];
          if (aggExists) {
            const buckets = aggSnap.data().buckets || [];
            body = buckets
              .filter(b => b.ts >= sinceMs && (b.ts + centerMs) < tailStart)
              .map(b => ({
                ts: b.ts + centerMs,
                v1: b.v1 && b.v1.n > 0 ? b.v1.avg : null,
                v2: b.v2 && b.v2.n > 0 ? b.v2.avg : null,
                // Average mA (rollup) → sensor-derived depth/gallons; pct fallback on older buckets.
                m1: (b.v1 && typeof b.v1.ma === 'number') ? b.v1.ma : null,
                m2: (b.v2 && typeof b.v2.ma === 'number') ? b.v2.ma : null,
                s2: !!(b.v2 && b.v2.n > 0),
              }))
              .filter(b => b.v1 != null || b.v2 != null);
          }
          const rawSince = aggExists ? tailStart : sinceMs;
          const q = query(
            collection(db, 'devices', selected, 'readings'),
            where('timestamp', '>=', new Date(rawSince)),
            orderBy('timestamp', 'asc')
          );
          const snap = await reader.getDocs(q);
          const tail = snap ? snap.docs.map(d => {
            const dd = d.data();
            return { ts: dd.timestamp?.toMillis() ?? 0, v1: dd.p1 ?? null, v2: dd.p2 ?? null, m1: dd.mA1 ?? null, m2: dd.mA2 ?? null, s2: dd.s2 ?? false };
          }).filter(r => r.ts > 0) : [];
          chartData = [...body, ...tail];
          t2Present = chartData.some(r => r.s2);
        } else {
          const q = query(
            collection(db, 'devices', selected, 'readings'),
            where('timestamp', '>=', new Date(sinceMs)),
            orderBy('timestamp', 'asc')
          );
          const snap = await reader.getDocs(q);
          const raw = snap ? snap.docs.map(d => {
            const dd = d.data();
            return { ts: dd.timestamp?.toMillis() ?? 0, v1: dd.p1 ?? null, v2: dd.p2 ?? null, m1: dd.mA1 ?? null, m2: dd.mA2 ?? null, s2: dd.s2 ?? false };
          }).filter(r => r.ts > 0) : [];
          chartData = raw;
          t2Present = raw.some(r => r.s2);
        }
        return { chartData, t2Present };
      };

      const cacheReader  = { getDoc: (ref) => cachedDoc(ref), getDocs: (q) => cachedDocs(q) };
      const serverReader = { getDoc: (ref) => withRetry(() => getDoc(ref)), getDocs: (q) => withRetry(() => getDocs(q)) };

      // 1) Instant paint from cache (best-effort; a miss just falls through).
      try {
        const c = await loadChart(cacheReader);
        if (c.chartData.length) { setHasT2(c.t2Present); setData(c.chartData); setLoading(false); }
      } catch { /* cache miss — fall through to the server read */ }

      // 2) Authoritative server read → revalidate + repaint.
      const s = await loadChart(serverReader);
      setHasT2(s.t2Present);
      setData(s.chartData);
      setLoading(false);

      // Events (server, best-effort — never gate the chart on this).
      try {
        const evQ = query(
          collection(db, 'devices', selected, 'events'),
          where('timestamp', '>=', new Date(sinceMs)),
          orderBy('timestamp', 'asc')
        );
        const evSnap = await withRetry(() => getDocs(evQ));
        setEvents(evSnap.docs.map(d => {
          const dd = d.data();
          return { ts: dd.timestamp?.toMillis() ?? 0, type: dd.type ?? -1, tank: dd.tank ?? 0 };
        }).filter(e => e.ts > 0));
      } catch { /* events are non-critical */ }

      // Reset cursor when data changes
      setCursorIdx(null);
      setPinned(false);
    } catch (err) {
      console.error('Trends fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [selected, range]);

  // Clear the chart on a real context switch (device or range) so we don't briefly
  // show the previous device/range's data. A resume-refetch keeps `selected`/
  // `range` the same, so it does NOT clear — the old chart stays up (stale) while
  // the background refresh runs.
  useEffect(() => { setData([]); }, [selected, range.value]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Trends is one-shot (not live-subscribed), so when the PWA resumes from a
  // background its data is stale and its reads may have died on the frozen
  // connection. Re-fetch on resume (connection.js has already reconnected).
  useEffect(() => {
    const onResume = () => fetchHistory();
    window.addEventListener(APP_RESUME_EVENT, onResume);
    return () => window.removeEventListener(APP_RESUME_EVENT, onResume);
  }, [fetchHistory]);

  // Pull-to-refresh re-runs the history fetch (Trends is not live-subscribed),
  // and re-reads the leak bands too.
  const [leakRefresh, setLeakRefresh] = useState(0);
  const refreshAll = useCallback(async () => {
    setLeakRefresh((n) => n + 1);
    await fetchHistory();
  }, [fetchHistory]);
  const { pull, refreshing } = usePullToRefresh(refreshAll);

  const currentDevice = devices.find(d => d.deviceId === selected);

  // ── Free tier ──────────────────────────────────────────
  // Free (no Plus, past its first day): 48 h and 1 wk only, and the chart
  // stops at this morning's 06:00 update — the prior days' hourly readings, a
  // day behind. The data is all still stored; Plus shows it live and 6 months
  // back. A shared user of a monitor without Plus sees why sharing is paused.
  const tier = currentDevice ? tierOf(currentDevice) : 'plus';
  const isFree = tier === 'free';
  const isOwnerHere = currentDevice?.role === 'owner';
  const sharingPaused = !!currentDevice && !isOwnerHere && !plusIsOn(currentDevice.plus);
  const takenAt = currentDevice?.dailyReading?.takenAt;
  const clipMs = isFree && takenAt && typeof takenAt.toMillis === 'function' ? takenAt.toMillis() : null;
  const data = useMemo(() => (clipMs ? rawData.filter((p) => p.ts <= clipMs) : rawData), [rawData, clipMs]);
  const [plusPromptOpen, setPlusPromptOpen] = useState(false);
  useEffect(() => {
    if (isFree && !FREE_RANGES.includes(range.value)) setRange(RANGES.find((r) => r.value === '1w'));
  }, [isFree, range.value]);

  // Metric availability across the currently-shown tanks. Offered if at least
  // one shown tank supports it; unsupported metrics grey out.
  const shownTanks = [];
  if (showT1) shownTanks.push(currentDevice?.tank1);
  if (showT2 && hasT2) shownTanks.push(currentDevice?.tank2);
  const avail = { depth: false, percent: false, gallons: false };
  shownTanks.forEach(t => {
    const a = tankMetricAvail(t);
    avail.depth   = avail.depth   || a.depth;
    avail.percent = avail.percent || a.percent;
    avail.gallons = avail.gallons || a.gallons;
  });

  // Fall back if the selected metric isn't available — percent, then depth,
  // then gallons. Lands a not-fully-set-up tank on Depth automatically.
  useEffect(() => {
    if (!avail[metric]) {
      const next = ['percent', 'depth', 'gallons'].find(m => avail[m]);
      if (next) setMetric(next);
    }
  }, [avail.depth, avail.percent, avail.gallons]); // eslint-disable-line react-hooks/exhaustive-deps

  // If tank 2 drops out of the current range (hasT2 flips false) while it was
  // the only shown series, the tank selector hides and the chart would render
  // nothing. Reconcile by forcing tank 1 back on so a line is always visible.
  useEffect(() => {
    if (!hasT2 && !showT1) setShowT1(true);
  }, [hasT2, showT1]);

  // Stored points -> selected-metric units.
  const displayData = useMemo(() => {
    const mapped = data.map(pt => ({
      ts: pt.ts,
      v1: seriesValue(pt, 1, metric, currentDevice),
      v2: seriesValue(pt, 2, metric, currentDevice),
    }));
    // Light noise-smoothing on sub-daily views. The raw sensor mA jitters a
    // little, which shows up as tiny stair-steps in depth/gallons near a
    // boundary. A short CENTERED moving average (skipping nulls/gaps) settles
    // the line without lag or hiding real trends. Because percent, depth and
    // gallons are all affine in mA across the usable band, smoothing the
    // derived series is equivalent to smoothing mA and keeps them consistent.
    // Daily ranges are already server-side averaged, so we leave them alone.
    if (range.daily || mapped.length < 5) return mapped;
    const half = range.value === '1w' ? 3 : 2;
    const smooth = (key) => {
      const src = mapped.map(m => m[key]);
      return src.map((v, i) => {
        if (v == null) return null;
        let sum = 0, n = 0;
        for (let j = i - half; j <= i + half; j++) {
          if (j >= 0 && j < src.length && src[j] != null) { sum += src[j]; n++; }
        }
        return n ? sum / n : v;
      });
    };
    const s1 = smooth('v1');
    const s2 = smooth('v2');
    return mapped.map((m, i) => ({ ts: m.ts, v1: s1[i], v2: s2[i] }));
  }, [data, metric, currentDevice, range]);

  // Full-scale Y max for the current metric so the chart always frames the
  // tank's whole range (0..full) instead of auto-zooming — a stable half-full
  // tank should look half full. Percent is always 0–100; depth/gallons use the
  // tank's configured full depth / capacity. Null when the tank isn't set up
  // enough to know its full scale (then drawChart auto-ranges as before).
  const yFullScale = useMemo(() => {
    if (metric === 'percent') return 100;
    const tanks = [];
    if (showT1) tanks.push(currentDevice?.tank1);
    if (hasT2 && showT2) tanks.push(currentDevice?.tank2);
    let max = 0;
    tanks.forEach(t => {
      if (!t) return;
      if (metric === 'depth'   && typeof t.fullFt   === 'number') max = Math.max(max, t.fullFt);
      if (metric === 'gallons' && typeof t.capacity === 'number') max = Math.max(max, t.capacity);
    });
    return max > 0 ? max : null;
  }, [metric, showT1, showT2, hasT2, currentDevice]);

  // Leak regions overlapping the current viewport, limited to the shown tanks
  // (dev mock for now; becomes a leakStats read when the backend is live).
  // Fetch leak regions for the visible range — only for leak-enabled devices
  // (0 reads otherwise); the ?leakdemo mock overrides for preview.
  const leakLive = useMemo(() => (currentDevice ? {
    episodes: currentDevice.usageEpisode || null,
    lastSettled: currentDevice.usageLastSettled || null,
    offline: false,   // the chart already stops at the last reading
  } : null), [currentDevice]);
  const fetchedRegions = useLeakRegions(
    selected, Date.now() - range.hours * 3600000, currentDevice?.leakDetection === true, currentDevice?.alerts,
    leakLive, leakRefresh);

  const leakRegions = useMemo(() => {
    if (displayData.length === 0) return [];
    const oldest = displayData[0].ts;
    const newest = displayData[displayData.length - 1].ts;
    const shown = new Set([...(showT1 ? [1] : []), ...(hasT2 && showT2 ? [2] : [])]);
    return fetchedRegions.filter(rg => {
      if (!shown.has(rg.tankNum)) return false;
      const e = rg.endTs == null ? newest : rg.endTs;
      return e > oldest && rg.startTs < newest;   // overlaps the visible range
    });
  }, [displayData, showT1, showT2, hasT2, fetchedRegions]);

  // Which kinds are actually on screen — the legend shows only these, so the
  // color key never appears when there's nothing to explain.
  const visibleLeakKinds = useMemo(() => {
    const s = new Set(leakRegions.map(r => r.kind));
    return { slow: s.has('slow'), rapid: s.has('rapid'), away: s.has('away') };
  }, [leakRegions]);

  // Redraw when the derived series, toggles, cursor, or viewport change
  useEffect(() => {
    if (!canvasRef.current || displayData.length === 0) return;
    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      // Size the backing store at device pixel ratio for crisp text; drawChart
      // draws in CSS px via a matching transform.
      const dpr = window.devicePixelRatio || 1;
      canvas.width  = Math.round(canvas.offsetWidth  * dpr);
      canvas.height = Math.round(canvas.offsetHeight * dpr);
      drawChart(canvas, displayData, showT1, hasT2 && showT2, events, range, cursorIdx, METRIC_UNIT[metric], yFullScale, leakRegions);
    };
    draw();
    window.addEventListener('resize', draw);
    return () => window.removeEventListener('resize', draw);
  }, [displayData, showT1, showT2, hasT2, events, range, cursorIdx, metric, yFullScale, leakRegions]);
  const tankSelection =
    showT1 && showT2 ? 'both' :
    showT1           ? 't1'   :
    showT2           ? 't2'   : 'none';

  function selectTank(value) {
    if (value === 'both') { setShowT1(true);  setShowT2(true);  }
    if (value === 't1')   { setShowT1(true);  setShowT2(false); }
    if (value === 't2')   { setShowT1(false); setShowT2(true);  }
  }

  // ── Cursor interaction handlers ─────────────────────────────────────────

  // Convert a clientX from a mouse/touch event into a data index.
  function clientXToIndex(clientX) {
    if (!canvasRef.current || data.length === 0) return null;
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const pad    = { l: 38, r: 12 };
    // CSS-pixel width — clientX/rect are CSS px, and canvas.width is now the
    // device-scaled backing store, so use offsetWidth to match.
    const cw     = canvas.offsetWidth - pad.l - pad.r;
    const xInCanvas = clientX - rect.left;
    if (xInCanvas < pad.l || xInCanvas > pad.l + cw) return null;
    const tsOldest = data[0].ts;
    const tsNewest = data[data.length - 1].ts;
    const tsSpan   = Math.max(tsNewest - tsOldest, 1);
    const cursorTs = tsOldest + ((xInCanvas - pad.l) / cw) * tsSpan;
    return findNearestIndex(data, cursorTs);
  }

  function handleMouseMove(e) {
    if (pinned) return;
    setCursorIdx(clientXToIndex(e.clientX));
  }
  function handleMouseLeave() {
    if (pinned) return;
    setCursorIdx(null);
  }
  function handleClick(e) {
    const idx = clientXToIndex(e.clientX);
    if (idx == null) {
      setPinned(false);
      setCursorIdx(null);
      return;
    }
    // Toggle pin: clicking the same point unpins
    if (pinned && idx === cursorIdx) {
      setPinned(false);
      setCursorIdx(null);
    } else {
      setPinned(true);
      setCursorIdx(idx);
    }
  }
  function handleTouchStart(e) {
    if (e.touches.length === 0) return;
    // No preventDefault needed — .chart-canvas sets touch-action:none, which
    // already blocks page scroll/gestures. Calling it here throws a passive-
    // listener warning under React's synthetic touch events.
    const t = e.touches[0];
    const idx = clientXToIndex(t.clientX);
    if (idx == null) {
      setPinned(false);
      setCursorIdx(null);
      return;
    }
    setPinned(true);
    setCursorIdx(idx);
  }
  function handleTouchMove(e) {
    if (e.touches.length === 0) return;
    const t = e.touches[0];
    const idx = clientXToIndex(t.clientX);
    if (idx != null) {
      setPinned(true);
      setCursorIdx(idx);
    }
  }

  // ── Tooltip data ────────────────────────────────────────────────────────

  function buildTooltip() {
    if (cursorIdx == null || cursorIdx < 0 || cursorIdx >= displayData.length) return null;
    const pt = displayData[cursorIdx];
    const rows = [];
    if (showT1 && pt.v1 != null) {
      rows.push({ color: 'reef', name: currentDevice?.tank1Name || 'Tank 1', val: pt.v1 });
    }
    if (hasT2 && showT2 && pt.v2 != null) {
      rows.push({ color: 'moss', name: currentDevice?.tank2Name || 'Tank 2', val: pt.v2 });
    }
    rows.sort((a, b) => b.val - a.val);
    const relTime = formatAbsolute(pt.ts, range);
    // Combined total only makes sense for an additive metric (gallons) when
    // both tanks are shown and both have a value at this point.
    const total = (metric === 'gallons' && rows.length === 2)
      ? rows[0].val + rows[1].val
      : null;
    // If this point falls inside a leak region, name it in the tooltip.
    const rg = leakRegions.find(r =>
      pt.ts >= r.startTs && pt.ts <= (r.endTs == null ? Infinity : r.endTs));
    const leak = rg
      ? (rg.kind === 'rapid' ? 'Usage alert'
        : rg.kind === 'away' ? 'Possible leak while away' : 'Slow leak')
      : null;
    return { rows, relTime, total, leak };
  }

  const tooltip = buildTooltip();
  const tooltipPx = canvasRef.current
    ? cursorPixelX(canvasRef.current, displayData, cursorIdx)
    : null;

  // Tooltip horizontal placement: keep within the chart container.
  // If too close to the right edge, flip to the left of the cursor.
  let tooltipStyle = {};
  if (tooltipPx != null && wrapRef.current) {
    const wrapW = wrapRef.current.offsetWidth;
    const tooltipApproxW = 200;
    const flipRight = tooltipPx + tooltipApproxW + 16 > wrapW;
    if (flipRight) {
      tooltipStyle = { right: `${wrapW - tooltipPx + 8}px` };
    } else {
      tooltipStyle = { left: `${tooltipPx + 8}px` };
    }
  }

  const hasCalEvents = events.some(e => EVENT_TYPES[e.type]?.show);

  return (
    <div className="app-container">
      <TopBar subtitle={currentDevice?.name || 'Trends'} />

      <div className="page-content">

        {(pull > 0 || refreshing) && (
          <div className="ptr-indicator" style={{ height: refreshing ? 44 : pull }}>
            <div className={`ptr-spinner ${refreshing ? 'ptr-spinner--spin' : ''}`} style={{ opacity: refreshing ? 1 : Math.min(1, pull / 70) }} />
          </div>
        )}

        {devices.length > 1 && (
          <div className="device-selector">
            {devices.map(d => (
              <button
                key={d.deviceId}
                type="button"
                className={`device-pill ${selected === d.deviceId ? 'active' : ''}`}
                onClick={() => setSelected(d.deviceId)}
              >
                {d.name}
              </button>
            ))}
          </div>
        )}

        {sharingPaused ? (
          <SharingPaused ownerEmail={currentDevice?.ownerEmail} />
        ) : (
        <>
        {isFree && (
          <div className="free-daily-note">
            <span>
              The Free plan shows up to 7 days.
              {isOwnerHere && (
                <>
                  {' '}For live data and 6 months of history,{' '}
                  <button type="button" className="linklike" onClick={() => setPlusPromptOpen(true)}>upgrade to SoKo Plus</button>.
                </>
              )}
            </span>
          </div>
        )}

        <div className="trends-controls">
          <div className="trends-range" role="group" aria-label="Time range">
            {RANGES.map(r => {
              const locked = isFree && !FREE_RANGES.includes(r.value);
              return (
                <button
                  key={r.value}
                  type="button"
                  className={`range-pill ${range.value === r.value ? 'active' : ''} ${locked ? 'range-pill--locked' : ''}`}
                  aria-label={locked ? `${r.label} (Plus)` : undefined}
                  onClick={() => (locked ? setPlusPromptOpen(true) : setRange(r))}
                >
                  {locked && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginRight: 3, verticalAlign: '-1px' }}>
                      <rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
                    </svg>
                  )}
                  {r.label}
                </button>
              );
            })}
          </div>

          <div className="trends-units" role="group" aria-label="Units">
            <span className="trends-units__label">Units</span>
            <div className="trends-units__group">
              {METRICS.map(m => {
                const on = avail[m.value];
                return (
                  <button
                    key={m.value}
                    type="button"
                    className={`trends-unit-pill ${metric === m.value ? 'active' : ''}`}
                    disabled={!on}
                    title={on ? '' : 'Finish tank setup to use this view'}
                    onClick={() => on && setMetric(m.value)}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {hasT2 && (
          <div className="range-row range-row--tanks">
            {[
              { label: 'Both',                                value: 'both' },
              { label: currentDevice?.tank1Name || 'Tank 1',  value: 't1'   },
              { label: currentDevice?.tank2Name || 'Tank 2',  value: 't2'   },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`range-pill ${tankSelection === opt.value ? 'active' : ''}`}
                onClick={() => selectTank(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}


        {currentDevice && !avail.percent && (
          <p className="chart-footnote" style={{ marginTop: 2 }}>
            Showing depth readings.{' '}
            <button
              type="button"
              onClick={() => (window.location.href = '/settings')}
              style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'var(--color-reef)', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Finish tank setup
            </button>{' '}
            to see percent and gallons.
          </p>
        )}

        <div className="card chart-card">
          <div className="chart-eyebrow">
            {METRIC_LABEL[metric]} · {range.label}{range.daily ? ' (daily averages)' : ''}
          </div>

          {(loading && data.length === 0) ? (
            <div className="chart-empty" style={{ flexDirection: 'column', gap: 14 }}>
              <div className="spinner" />
              {slowLoad && (
                <button type="button" className="btn btn-primary"
                  onClick={() => window.location.reload()}>
                  Reload
                </button>
              )}
            </div>
          ) : data.length === 0 ? (
            <div className="chart-empty chart-empty--text">
              No data yet for this range
            </div>
          ) : (
            <div
              className="chart-wrap"
              ref={wrapRef}
            >
              <canvas
                ref={canvasRef}
                className="chart-canvas"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
              />
              {tooltip && tooltipPx != null && (
                <div className="chart-tooltip" style={tooltipStyle}>
                  {tooltip.leak && (
                    <div className="chart-tooltip__leak">{tooltip.leak}</div>
                  )}
                  <div className="chart-tooltip__time">{tooltip.relTime}</div>
                  {tooltip.rows.map((row, i) => (
                    <div key={i} className="chart-tooltip__row">
                      {tooltip.rows.length > 1 && (
                        <span className={`chart-tooltip__swatch chart-tooltip__swatch--${row.color}`} />
                      )}
                      <span className="chart-tooltip__name">{row.name}</span>
                      <span className="chart-tooltip__values">
                        {fmtValue(row.val, metric, prefs.depthFormat)}
                      </span>
                    </div>
                  ))}
                  {tooltip.total != null && (
                    <>
                      <div className="chart-tooltip__divider" />
                      <div className="chart-tooltip__row chart-tooltip__row--total">
                        <span className="chart-tooltip__name">Total</span>
                        <span className="chart-tooltip__values">
                          {fmtValue(tooltip.total, metric, prefs.depthFormat)}
                        </span>
                      </div>
                    </>
                  )}
                  {pinned && (
                    <div className="chart-tooltip__hint">tap chart to clear</div>
                  )}
                </div>
              )}
            </div>
          )}

          {data.length > 0 && (
            <div className="chart-legend">
              {showT1 && (
                <div className="chart-legend__item">
                  <span className="chart-legend__swatch chart-legend__swatch--reef" />
                  <span>{currentDevice?.tank1Name || 'Tank 1'}</span>
                </div>
              )}
              {hasT2 && showT2 && (
                <div className="chart-legend__item">
                  <span className="chart-legend__swatch chart-legend__swatch--moss" />
                  <span>{currentDevice?.tank2Name || 'Tank 2'}</span>
                </div>
              )}
              {hasCalEvents && (
                <div className="chart-legend__item"
                  title="A calibration or tank-setting change. Percent and gallons before this reflect the updated calibration; depth is measured directly by the sensor and is unaffected.">
                  <span className="chart-legend__swatch chart-legend__swatch--cal" />
                  <span>Calibration</span>
                </div>
              )}
              {visibleLeakKinds.slow && (
                <div className="chart-legend__item">
                  <span className="chart-legend__swatch chart-legend__swatch--leak-slow" />
                  <span>Slow leak</span>
                </div>
              )}
              {visibleLeakKinds.away && (
                <div className="chart-legend__item">
                  <span className="chart-legend__swatch chart-legend__swatch--leak-slow" />
                  <span>Leak while away</span>
                </div>
              )}
              {visibleLeakKinds.rapid && (
                <div className="chart-legend__item">
                  <span className="chart-legend__swatch chart-legend__swatch--leak-rapid" />
                  <span>Usage alert</span>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="chart-footnote">
          Hover or tap the chart to see values · 1mo+ views show daily averages
        </p>

        {/* Desktop: explicit button (no pull gesture there). Mobile: hidden in
            favor of the pull-to-refresh hint below. */}
        <button
          type="button"
          className="btn btn-secondary trends-refresh-btn"
          onClick={fetchHistory}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh graph'}
        </button>
        <p className="trends-refresh-hint">
          {refreshing ? 'Refreshing…' : 'Pull down to refresh'}
        </p>
        </>
        )}

        <PlusPromptModal
          open={plusPromptOpen}
          onClose={() => setPlusPromptOpen(false)}
          deviceId={selected}
          plus={currentDevice?.plus}
          isOwner={isOwnerHere}
        />

      </div>
      <BottomNav />
    </div>
  );
}